import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { registerUser } from '@/services/authApi';
import { getUserFriendlyMessage } from '@/services/apiError';
import FormMessage from '@/components/common/FormMessage';

export default function RegisterPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const canSubmit =
    Boolean(formData.username.trim()) &&
    Boolean(formData.email.trim()) &&
    Boolean(formData.password) &&
    Boolean(formData.confirmPassword) &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }

    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('两次密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await registerUser({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.username.trim(),
      });
      login(response.token, response.user);
      navigate('/dashboard');
    } catch (error: unknown) {
      setError(getUserFriendlyMessage(error, '注册失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-[calc(100vh-48px)] flex items-center justify-center px-4 py-16'>
      <div className='w-full max-w-[380px]'>
        <h1
          className='font-semibold text-apple-near-black mb-2'
          style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
        >
          注册
        </h1>
        <p className='text-apple-gray mb-8' style={{ fontSize: '0.9375rem' }}>
          已有账号？{' '}
          <Link to='/login' className='text-apple-blue-link hover:underline'>
            登录
          </Link>
        </p>

        <form className='space-y-5' onSubmit={handleSubmit}>
          {error ? <FormMessage tone='error' message={error} /> : null}

          <div className='space-y-1.5'>
            <label htmlFor='username' className='block text-[0.875rem] font-medium text-apple-near-black'>
              用户名
            </label>
            <input
              id='username'
              type='text'
              required
              autoComplete='username'
              className='input'
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </div>

          <div className='space-y-1.5'>
            <label htmlFor='email' className='block text-[0.875rem] font-medium text-apple-near-black'>
              邮箱
            </label>
            <input
              id='email'
              type='email'
              required
              autoComplete='email'
              className='input'
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className='space-y-1.5'>
            <label htmlFor='password' className='block text-[0.875rem] font-medium text-apple-near-black'>
              密码
            </label>
            <input
              id='password'
              type='password'
              required
              autoComplete='new-password'
              className='input'
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          <div className='space-y-1.5'>
            <label htmlFor='confirmPassword' className='block text-[0.875rem] font-medium text-apple-near-black'>
              确认密码
            </label>
            <input
              id='confirmPassword'
              type='password'
              required
              autoComplete='new-password'
              className='input'
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            />
          </div>

          <button
            type='submit'
            className='btn btn-primary w-full mt-2'
            disabled={!canSubmit}
          >
            {loading ? '注册中…' : '创建账号'}
          </button>
        </form>
      </div>
    </div>
  );
}
